/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  SNMPDevice,
  SNMPDeviceStatus,
  SNMPInterface,
  SNMPMacEntry,
  getSNMPDeviceStatus,
  getSNMPDeviceInterfaces,
  getSNMPDeviceMacTable,
} from "@/api/snmp";
import { SNMP_REFRESH_INTERVALS } from "@/constants/snmpRefreshIntervals";

interface UseSNMPDeviceOptions {
  device: SNMPDevice;
  includeMacTable?: boolean;
}

export const useSNMPDevice = ({ device, includeMacTable = false }: UseSNMPDeviceOptions) => {
  const queryClient = useQueryClient();

  const statusQuery = useQuery<SNMPDeviceStatus>({
    queryKey: ["snmp-device-status", device.id],
    queryFn: () => getSNMPDeviceStatus(device.id),
    refetchInterval: device.enabled ? SNMP_REFRESH_INTERVALS.STATUS : false,
    staleTime: SNMP_REFRESH_INTERVALS.STATUS / 2,
    enabled: device.enabled,
  });

  const interfacesQuery = useQuery<SNMPInterface[]>({
    queryKey: ["snmp-device-interfaces", device.id],
    queryFn: () => getSNMPDeviceInterfaces(device.id),
    refetchInterval: device.enabled ? SNMP_REFRESH_INTERVALS.INTERFACES : false,
    staleTime: SNMP_REFRESH_INTERVALS.INTERFACES / 2,
    enabled: device.enabled,
  });

  const macTableQuery = useQuery<SNMPMacEntry[]>({
    queryKey: ["snmp-device-mac-table", device.id],
    queryFn: () => getSNMPDeviceMacTable(device.id),
    refetchInterval: device.enabled ? SNMP_REFRESH_INTERVALS.MAC_TABLE : false,
    staleTime: SNMP_REFRESH_INTERVALS.MAC_TABLE / 2,
    enabled: device.enabled && includeMacTable,
  });

  const invalidateDevice = () => {
    queryClient.invalidateQueries({ queryKey: ["snmp-devices"] });
    queryClient.invalidateQueries({ queryKey: ["snmp-device-status", device.id] });
    queryClient.invalidateQueries({ queryKey: ["snmp-device-interfaces", device.id] });
  };

  return {
    status: statusQuery.data,
    interfaces: interfacesQuery.data,
    macTable: macTableQuery.data,
    isLoadingStatus: statusQuery.isLoading,
    isLoadingInterfaces: interfacesQuery.isLoading,
    isLoadingMacTable: macTableQuery.isLoading,
    invalidateDevice,
  };
};
